import type { ConfiguracaoLoja, TotemNota } from '../api';

const TEMPLATE_CUPOM = 'https://pdv.sischef.com/impressao-online.html'

export const PONTOS_80MM = 576
const APROVEITAMENTO = 0.9 * 1.5
const CHAVE_IMPRESSOS = 'totem-documentos-impressos'
const VALIDADE_IMPRESSO_MS = 24 * 60 * 60 * 1000
const TIMEOUT_RASTERIZADOR_MS = 20_000

export function viewportPara(pontos: number): number {
  return Math.floor(pontos / APROVEITAMENTO)
}

function urlRasterizador(servidor: string | null): string {
  const proprio = (import.meta.env.VITE_RASTERIZADOR_URL as string | undefined)?.trim()
  if (proprio) return proprio
  if (!servidor) throw new Error('nenhum rasterizador configurado')

  return `${servidor}/api/impressaoSmartpos/htmlToImage`
}

export interface DocumentoImpressao {
  identificador?: string
  conteudo?: { tipo?: string; linhas?: unknown[] } | null
}

export type DesfechoImpressao = 'IMPRESSO' | 'JA_IMPRESSO' | 'SEM_DOCUMENTO' | 'FALHOU'

function imprimivel(doc: DocumentoImpressao): boolean {
  return (doc.conteudo?.tipo ?? '') !== 'URL' && Array.isArray(doc.conteudo?.linhas)
}

export function imprimeNoAparelho(): boolean {
  return typeof window !== 'undefined' && typeof window.pdvDesktop?.imprimirCupom === 'function'
}

export async function temImpressoraNoAparelho(): Promise<boolean> {
  if (typeof window === 'undefined') return false

  return (await window.pdvDesktop?.temImpressora?.()) === true
}

/** Largura da bobina do aparelho; sem resposta válida, o rasterizador fica nos 80 mm. */
async function larguraDoAparelho(): Promise<number | undefined> {
  try {
    const pontos = await window.pdvDesktop?.larguraImpressora?.()

    return typeof pontos === 'number' && Number.isFinite(pontos) && pontos > 0 ? pontos : undefined
  } catch {
    return undefined
  }
}

export function chaveDaTransacao(...candidatos: Array<string | null | undefined>): string {
  const preenchido = candidatos.map((candidato) => candidato?.trim()).find((candidato): candidato is string => !!candidato)

  return preenchido ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export function documentoComprovante(comprovante: string | null | undefined): DocumentoImpressao | null {
  if (!comprovante?.trim()) return null

  const linhas = comprovante.split(/\r\n|\r|\n/).map((texto) => ({ texto, alinhamento: 'E' }))

  return { identificador: 'COMPROVANTE_TEF', conteudo: { tipo: 'TEXT', linhas } }
}

async function rasterizar(servidor: string | null, doc: DocumentoImpressao, largura?: number): Promise<string> {
  const resp = await fetch(urlRasterizador(servidor), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer totem' },
    body: JSON.stringify({
      link: TEMPLATE_CUPOM,
      data: [doc],
      width: viewportPara(largura ?? PONTOS_80MM),
    }),
    signal: AbortSignal.timeout(TIMEOUT_RASTERIZADOR_MS),
  })

  if (!resp.ok) throw new Error(`rasterizador respondeu ${resp.status}`)

  const { base64 } = (await resp.json()) as { base64?: string }
  if (!base64) throw new Error('rasterizador devolveu imagem vazia')

  return base64
}

async function imprimir(servidor: string | null, doc: DocumentoImpressao, largura?: number) {
  if (doc.conteudo?.tipo === 'TEXT') {
    return window.pdvDesktop?.imprimirTexto?.(doc.conteudo.linhas)
  }

  return window.pdvDesktop?.imprimirCupom?.(await rasterizar(servidor, doc, largura ?? await larguraDoAparelho()))
}

function lerImpressos(agora: number): Record<string, number> {
  try {
    const salvo = JSON.parse(localStorage.getItem(CHAVE_IMPRESSOS) ?? '{}') as Record<string, unknown>

    return Object.fromEntries(
      Object.entries(salvo ?? {}).filter(
        (par): par is [string, number] => typeof par[1] === 'number' && agora - par[1] < VALIDADE_IMPRESSO_MS,
      ),
    )
  } catch {
    return {}
  }
}

export function jaImpresso(chave: string, agora = Date.now()): boolean {
  return chave in lerImpressos(agora)
}

function registrarImpresso(chave: string, agora = Date.now()): void {
  try {
    localStorage.setItem(CHAVE_IMPRESSOS, JSON.stringify({ ...lerImpressos(agora), [chave]: agora }))
  } catch {
    // sem localStorage o totem só perde a proteção contra reimpressão
  }
}

let filaDoAparelho: Promise<unknown> = Promise.resolve()
const emAndamento = new Set<string>()

export function imprimirUmaVez(
  chave: string,
  obter: () => Promise<DocumentoImpressao[]>,
  servidor: string | null,
): Promise<DesfechoImpressao> {
  if (emAndamento.has(chave) || jaImpresso(chave)) return Promise.resolve('JA_IMPRESSO')

  emAndamento.add(chave)

  const vez = filaDoAparelho.then(async (): Promise<DesfechoImpressao> => {
    try {
      const documentos = (await obter()).filter(imprimivel)
      if (!documentos.length) return 'SEM_DOCUMENTO'

      for (const [indice, doc] of documentos.entries()) {
        const chaveDoDocumento = `${chave}#${indice}:${doc.identificador ?? 'doc'}`
        if (jaImpresso(chaveDoDocumento)) continue

        const r = await imprimir(servidor, doc)

        if (!r?.ok) {
          console.warn(`[impressao-totem] ${chave} não saiu: ${r?.motivo ?? 'sem resposta'}`)
          return 'FALHOU'
        }

        registrarImpresso(chaveDoDocumento)
      }

      registrarImpresso(chave)
      return 'IMPRESSO'
    } catch (e) {
      console.warn(`[impressao-totem] falha em ${chave}`, e)
      return 'FALHOU'
    } finally {
      emAndamento.delete(chave)
    }
  })

  filaDoAparelho = vez.catch(() => undefined)

  return vez
}

export async function imprimirComInsistencia(
  chave: string,
  obter: () => Promise<DocumentoImpressao[]>,
  servidor: string | null,
  tentativas = 3,
  esperaMs = 2000,
): Promise<DesfechoImpressao> {
  for (let tentativa = 1; ; tentativa++) {
    const desfecho = await imprimirUmaVez(chave, obter, servidor)
    if (desfecho === 'IMPRESSO' || desfecho === 'JA_IMPRESSO' || tentativa >= tentativas) return desfecho

    await new Promise((resolver) => setTimeout(resolver, esperaMs))
  }
}

/** O que o totem de PEDIDOS imprime ao finalizar (o totem de checkout tem config própria). */
export interface OpcoesImpressaoTotem {
  imprimirSenha: boolean;
  imprimirResumo: boolean;
  nota: TotemNota;
}

type ConfigImpressao = Partial<
  Pick<ConfiguracaoLoja, 'totemImpressao' | 'totemImprimirSenha' | 'totemImprimirResumo' | 'totemNota'>
>;

/**
 * Lê as opções da loja. Backend anterior às opções só manda o enum legado — aí vale o mesmo
 * mapeamento do backfill (RESUMO = senha + resumo; NFCE = senha + nota SEMPRE).
 */
export function opcoesImpressaoTotem(config: ConfigImpressao | undefined): OpcoesImpressaoTotem {
  if (config?.totemNota === undefined) {
    const legado = config?.totemImpressao;
    return {
      imprimirSenha: legado === 'RESUMO' || legado === 'NFCE',
      imprimirResumo: legado === 'RESUMO',
      nota: legado === 'NFCE' ? 'SEMPRE' : 'NAO',
    };
  }
  return {
    imprimirSenha: config.totemImprimirSenha === true,
    imprimirResumo: config.totemImprimirResumo === true,
    nota: config.totemNota,
  };
}

/**
 * O enum legado que acompanha as opções (mesma regra do backend). O admin manda os dois: backend
 * anterior às opções só entende o enum e descartaria o resto em silêncio.
 */
export function legadoDasOpcoes(opcoes: OpcoesImpressaoTotem): ConfiguracaoLoja['totemImpressao'] {
  if (opcoes.nota !== 'NAO') return 'NFCE';
  if (opcoes.imprimirResumo) return 'RESUMO';
  return 'NENHUMA';
}

type ConfigDosTotens = Partial<Pick<ConfiguracaoLoja, 'totemPerguntarCpfNota' | 'totemImprimirComprovanteTef'>>;

/** Os dois totens perguntam o CPF da nota, salvo se o totem desligou. Backend anterior não manda: pergunta. */
export function perguntaCpfNaNota(config: ConfigDosTotens | undefined): boolean {
  return config?.totemPerguntarCpfNota !== false;
}

/** A via do cliente da maquininha sai no papel, salvo se o totem desligou. */
export function imprimeComprovanteTef(config: ConfigDosTotens | undefined): boolean {
  return config?.totemImprimirComprovanteTef !== false;
}

/** Alguma coisa sai no papel? Sem nada marcado, o totem não precisa de impressora. */
export function totemImprimeAlgo(opcoes: OpcoesImpressaoTotem): boolean {
  return opcoes.imprimirSenha || opcoes.imprimirResumo || opcoes.nota !== 'NAO';
}

/**
 * Onde está a impressão da NFC-e em "Perguntar ao cliente", do ponto de vista da tela:
 * ainda não respondeu, mandou imprimir (e o pedido saiu/está saindo), recusou, ou pediu e falhou.
 */
export type EtapaImpressaoNota = 'PENDENTE' | 'ENVIANDO' | 'ENVIADA' | 'RECUSADA' | 'FALHOU';

/** Como a tela descreve o cupom da nota — muda o texto do bloco/faixa da NFC-e. */
export type ImpressaoCupomNota = 'AUTOMATICA' | 'A_PERGUNTAR' | 'SEM_CUPOM' | 'FALHOU';

/**
 * `aguardando` = a tela ainda segura o cliente (a pergunta pode vir). Solta a tela sem
 * resposta (SEFAZ estourou o prazo), não haverá pergunta — o cupom não sai.
 */
export function impressaoCupomDaNota(
  nota: TotemNota,
  etapa: EtapaImpressaoNota,
  aguardando: boolean,
): ImpressaoCupomNota {
  if (nota !== 'PERGUNTAR') return 'AUTOMATICA';
  if (etapa === 'ENVIANDO' || etapa === 'ENVIADA') return 'AUTOMATICA';
  if (etapa === 'FALHOU') return 'FALHOU';
  if (etapa === 'RECUSADA') return 'SEM_CUPOM';
  return aguardando ? 'A_PERGUNTAR' : 'SEM_CUPOM';
}
